<script setup lang="ts">
import { type UserQuestion } from "@gasparigit/omnia-sdk";
import type { GenericObject } from "vee-validate";
import { z } from "zod";
import { processError, requiredError, successToast } from "~/lib/utils/errors";
import { remainQuestionString } from "~/lib/utils/messages";
import AreaField from "../fields/Area.vue";
import CustomerField from "../fields/Customer.vue";
import type { ConfigItem, FieldProps } from "../ui/auto-form";

const omniaSdk = useOmniaSdk();

const opened = defineModel<boolean>("opened");
const loading = ref<boolean>(false);
const emits = defineEmits(["success"]);

const { data: loggedUser } = useLoggedUser(null, false);

const { data: areas } = useAsyncData(`all-areas`, async () => {
  try {
    return await omniaSdk.v1.areas.list({ pagination: "none" });
  } catch (error) {
    processError(error);
  }
});

const {
  data: canCreate,
  refresh: refreshCanCreate,
  status: canCreateStatus,
} = useAsyncData(
  "enabled-customers",
  async () => {
    return await Promise.all(loggedUser.value.customers.map((c) => omniaSdk.v1.userQuestions.canCreate(c.id)));
  },
  {
    immediate: false,
    server: false,
  }
);

watch(opened, (newValue, oldValue) => {
  if (newValue && !oldValue) refreshCanCreate();
});

const enabledCustomers = computed(() => {
  if (!canCreate.value) return [];

  return loggedUser.value?.customers?.filter((c, i) => canCreate.value[i]);
});

const formSchema = z.object({
  title: z.string(requiredError).describe("Titolo"),
  question: z.string(requiredError).describe("Quesito"),
  customer_id: z.number(requiredError).describe("Cliente"),
  area_id: z.number(requiredError).describe("Area"),
});

const form = useForm({ validationSchema: toTypedSchema(formSchema) });

const fieldConfig: Record<string, ConfigItem> = {
  question: {
    component: "textarea",
  },
  customer_id: {
    label: "Cliente",
    component: defineComponent(
      (props: FieldProps) => () =>
        h(CustomerField, {
          customers: enabledCustomers.value,
          custom_title: (c) => `${c.name}${remainQuestionString(c) ? ` (${remainQuestionString(c)})` : ""}`,
          ...props,
        })
    ),
  },
  area_id: {
    label: "Area",
    component: defineComponent(
      (props: FieldProps) => () =>
        h(AreaField, {
          areas: areas.value.data,
          ...props,
        })
    ),
  },
};

const userQuestion = ref<UserQuestion | null>(null);

async function sendUserQuestion(values: GenericObject) {
  loading.value = true;

  try {
    userQuestion.value = {
      title: values["title"],
      question: values["question"],
      customer_id: values["customer_id"],
      area_id: values["area_id"],
    } as UserQuestion;

    await omniaSdk.v1.userQuestions.create(userQuestion.value);

    successToast("Quesito inviato con successo");

    emits("success");
  } catch (error) {
    processError(error);
  }

  loading.value = false;
}
</script>

<template>
  <Dialog v-model:open="opened">
    <DialogTrigger as-child>
      <slot />
    </DialogTrigger>
    <DialogScrollContent class="max-w-screen-xl" @pointer-down-outside.prevent>
      <DialogHeader>
        <DialogTitle>Poni il tuo quesito</DialogTitle>
        <DialogDescription>Leggi attentamente le condizioni di utilizzo, prima di inviare un quesito</DialogDescription>
      </DialogHeader>
      <Skeleton class="rounded-lg h-96" v-if="canCreateStatus !== 'success'" />
      <FrontUnauthorized v-else-if="enabledCustomers.length <= 0" />
      <div v-else>
        <div class="mb-8 text-base italic space-y-2">
          <p>Il Servizio Quesiti fornisce risposte tese ad orientare l’Ente alla risoluzione di dubbi di natura operativa e dunque non mette a disposizione un parere legale.</p>
          <p>
            Il quesito deve contenere un’unica domanda e deve essere redatto in maniera circoscritta; pertanto non potranno essere evasi quesiti prolissi con i quali viene richiesta la disamina
            dell’intero caso o fattispecie e allo stesso modo quesiti oltremodo generici con i quali vengono richiesti i singoli passaggi/adempimenti da compiersi per la realizzazione di una
            pratica/fascicolo. Non sono gestiti in nessun caso quesiti che richiedono la disamina o contemplano l'applicazione di leggi, regolamenti o prassi di origine regionale/locale (Qualora si
            necessitasse di una consulenza differente che esula dal servizio quesiti, si potrà inviare apposita richiesta di preventivo all’indirizzo
            <a class="link" href="mailto:marketing@egaspari.net">marketing@egaspari.net</a> ).
          </p>
          <p>Al quesito viene data risposta entro cinque giorni lavorativi. È possibile inviare un nuovo quesito solo dopo aver ricevuto risposta al quesito precedente.</p>
          <p>
            Non appena disponibile la risposta, viene trasmessa un’email di avviso all’utente che ha inserito il quesito. I quesiti, e le relative risposte, possono essere visualizzati esclusivamente
            dall’utente Omnia che ha trasmesso il quesito.
          </p>
          <p>Per le condizioni generali in merito all’utilizzo del portale Progetto Omnia, si rimanda al seguente indirizzo: <NuxtLink class="link" href="/note-legali">Note legali</NuxtLink></p>
        </div>
        <AutoForm @submit="sendUserQuestion" :form="form" :schema="formSchema" :field-config="fieldConfig" class="content-card-grid grid gap-4">
          <DialogFooter>
            <DialogClose>
              <Button variant="ghost" type="reset">Annulla</Button>
            </DialogClose>
            <Button type="submit" :disabled="loading || !form.meta.value.valid" :class="{ 'animate-pulse': loading }">Invia</Button>
          </DialogFooter>
        </AutoForm>
      </div>
    </DialogScrollContent>
  </Dialog>
</template>
